import { Box, Typography, alpha } from '@mui/material';
import { DeleteActionButton, EditActionButton, ViewActionButton } from '../../components/MRTDataGrid/TableActionButtons';
import { formatDate } from '../../app/utils';
import { MRT_ColumnDef as MRTColumnDef, MRT_Row as MRTRow } from 'material-react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useFormContext } from 'react-hook-form';
import {
  useGetDocumentCategoryQuery,
  useGetDocumentsByEntityIdQuery,
} from '../../app/redux-fetch/apiCoreQuery';
import AddButton from '../../components/Buttons/AddButton';
import ConditionalRender from '../../components/ConditionalRender/ConditionalRender';
import { useConfirmDialogContext } from '../../components/ConfirmDialog';
import MRTDataGridv2 from '../../components/MRTDataGrid/MRTDataGridv2';
import { SectionTitleTypography } from '../../components/Typographies/styles';
import { useDocumentHandle } from '../Assets/hooks/useDocumentHandle';
import DocumentFormModal from './DocumentFormModal';
import { IDocument } from './types';

type DocumentsProps = {
  categoryId: number
  tenantId: number
  customerId?: number
  entityId?: number
  entityType?: string
  entityName?: string
  isDisabled?: boolean
  showTitle?: boolean
  rowId?: string | number
}

const Documents = ({
  categoryId,
  tenantId,
  customerId,
  entityId,
  entityType,
  rowId,
  entityName,
  isDisabled = false,
  showTitle = true,
}: DocumentsProps) => {
  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [viewOpen, setViewOpen] = useState(false)
  const { formatMessage } = useIntl()
  const { confirm } = useConfirmDialogContext()

  const { setValue, watch } = useFormContext()

  const path = () => {
    if (entityType === 'PRICE') {
      return `prices.${rowId}.documents`
    }
    return 'documents'
  }

  const documentValues = watch(path()) || []

  const { data: documents, isSuccess: loaded } = useGetDocumentsByEntityIdQuery(
    { entityId: entityId ?? -1, categoryId },
    { refetchOnMountOrArgChange: true, skip: !entityId },
  )

  const { data: documentCategory } = useGetDocumentCategoryQuery({
    tenantId: tenantId ?? -1,
    customerId,
    categoryId,
  })

  const columns = useMemo<MRTColumnDef<IDocument>[]>(() => {
    return [
      {
        id: 'name',
        accessorFn: (row) => {
          return <Typography sx={{ fontWeight: 700 }}>{row.name}</Typography>
        },
        header: 'Name',
      },
      {
        accessorFn: (row) => {
          return formatDate(row.expiryDate)
        },
        header: 'Expiry Date',
      },
      {
        accessorFn: (row) => {
          return documentCategory?.documentTypes?.find((type) => type.id === row.documentTypeId)
            ?.name
        },
        header: 'Document Type',
      },
    ]
  }, [documentCategory])

  const [selectedDocument, setSelectedDocument] = useState(-1)

  const getDocumentToEdit = useCallback(() => {
    if (selectedDocument > -1) {
      return documentValues[selectedDocument]
    }
    return undefined
  }, [documentValues, selectedDocument])

  useEffect(() => {
    if (documents && entityId) {
      setValue(path(), documents)
    }
  }, [documents, setValue, entityId])

  const { removeDocument } = useDocumentHandle()

  const handleDelete = (rowId: any) => {
    const updatedDocuments = [...documentValues]
    const removedDocuments = updatedDocuments.splice(rowId, 1)
    if (removedDocuments.length > 0) {
      removedDocuments[0].fileId && removeDocument(removedDocuments[0].fileId)
    }
    setValue(path(), updatedDocuments)
  }

  const openDeleteConfirmModal = async (row: MRTRow<IDocument>) => {
    const confirmed = await confirm({
      title: 'Delete Document',
      message: 'Are you sure you want to delete this document?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      severity: 'warning',
      confirmButtonColor: 'error',
      cancelButtonColor: 'secondary',
    })

    if (confirmed) {
      handleDelete(row.index)
    }
  }

  const getDocumentMetadata = () => {
    return {
      entityType: entityType || 'DOCUMENT',
      entityName: entityName || `Document ${entityId}`,
      entityId,
    }
  }

  const handleCreateDocument = (document: IDocument) => {
    const metadata = getDocumentMetadata()
    const enrichedDocument = {
      ...document,
      entityType: metadata.entityType,
      entityName: metadata.entityName,
    }

    setValue(path(), [...documentValues, enrichedDocument])
    setCreateOpen(false)
    setSelectedDocument(-1)
  }

  return (
    <Box sx={{ pt: '16px' }}>
      {createOpen && (
        <DocumentFormModal
          tenantId={tenantId}
          customerId={customerId}
          isOpen={createOpen}
          categoryId={categoryId}
          handleCancel={function (): void {
            setCreateOpen((pv) => !pv)
          }}
          handleSubmit={handleCreateDocument}
        />
      )}
      {editOpen && (
        <DocumentFormModal
          tenantId={tenantId}
          customerId={customerId}
          categoryId={categoryId}
          isOpen={editOpen}
          handleCancel={function (): void {
            setEditOpen((pv) => !pv)
          }}
          documentToEdit={getDocumentToEdit()}
          handleSubmit={(document) => {
            const updatedDocs = [...documentValues]
            updatedDocs[selectedDocument] = document
            setValue(path(), updatedDocs)
            setEditOpen((pv) => !pv)
            setSelectedDocument(-1)
          }}
        />
      )}
      {viewOpen && (
        <DocumentFormModal
          categoryId={categoryId}
          tenantId={tenantId}
          customerId={customerId}
          isOpen={viewOpen}
          documentToEdit={getDocumentToEdit()}
          handleCancel={function (): void {
            setViewOpen((pv) => !pv)
          }}
        />
      )}
      <Box>
        <MRTDataGridv2
          initialState={{
            sorting: [
              {
                id: 'name',
                desc: false,
              },
            ],
          }}
          state={{ isLoading: !loaded && !!entityId }}
          leftHeadingComponent={
            showTitle ? <SectionTitleTypography>Documents</SectionTitleTypography> : <></>
          }
          rightHeadingComponent={
            <AddButton disabled={isDisabled} text={'New'} onClick={() => setCreateOpen(true)} />
          }
          columns={columns}
          data={documentValues}
          getRowId={(row) => row.id as any}
          enableEditing={true}
          enableRowActions
          enableColumnActions={false}
          enableColumnFilters={false}
          enablePagination={true}
          enableSorting={false}
          enableBottomToolbar={true}
          enableTopToolbar={false}
          positionActionsColumn='last'
          muiTableBodyProps={{
            sx: (theme) => ({
              '& tr:nth-of-type(odd)': {
                backgroundColor: alpha(theme.palette.secondary.main, 0.08),
              },
            }),
          }}
          muiTableHeadCellProps={{
            sx: {
              color: '#636363',
              fontSize: '14px',
            },
          }}
          renderRowActions={({ row }: any) => {
            return (
              <Box sx={{ display: 'flex' }}>
                <ViewActionButton
                  onClick={() => {
                    setSelectedDocument(row.index)
                    setViewOpen(true)
                  }}
                />
                <ConditionalRender condition={!isDisabled}>
                  <EditActionButton
                    onClick={() => {
                      setSelectedDocument(row.index)
                      setEditOpen(true)
                    }}
                  />
                </ConditionalRender>
                <ConditionalRender condition={!isDisabled}>
                  <DeleteActionButton
                    onClick={() => {
                      openDeleteConfirmModal(row)
                      setSelectedDocument(-1)
                    }}
                  />
                </ConditionalRender>
              </Box>
            )
          }}
        />
      </Box>
    </Box>
  )
}

export default Documents
